import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, Check, AlertCircle, X, Eye } from 'lucide-react';
import { api } from '../auth/authStore';

interface ChecklistItem {
  label: string;
  description?: string;
  required: boolean;
  order: number;
}

interface WorkflowTemplate {
  id: string;
  _id?: string; // For backward compatibility
  country: string;
  name: string;
  description: string;
  type?: string;
  priority?: string;
  billingPeriod?: string;
  recurrencePattern?: string;
  estimatedHours?: number;
  billing?: {
    feeType?: string;
    fixedAmount?: number;
    billableRate?: number;
  };
  checklist?: ChecklistItem[];
  tags?: string[];
  recurrence?: {
    isRecurring?: boolean;
    frequency?: string;
    interval?: number;
    dayOfWeek?: number;
    dayOfMonth?: number;
    monthOfYear?: number;
    endDate?: string;
    maxOccurrences?: number;
  };
  isActive: boolean;
  version: number;
}

type BillingForm = {
  feeType: string;
  fixedAmount?: number;
  billableRate?: number;
};

type RecurrenceForm = {
  isRecurring: boolean;
  frequency?: string;
  interval?: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  monthOfYear?: number;
  endDate?: string;
  maxOccurrences?: number;
};

type WorkflowTemplateForm = Omit<WorkflowTemplate, 'id' | 'version' | 'billing' | 'recurrence' | 'checklist' | 'tags'> & {
  id?: string;
  version?: number;
  billing: BillingForm;
  recurrence: RecurrenceForm;
  checklist: ChecklistItem[];
  tags: string[];
};

interface CountryTemplates {
  [key: string]: WorkflowTemplate[];
}

// Modal Component for Viewing/Adding/Editing Workflow Templates
const WorkflowModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Partial<WorkflowTemplate>, templateId?: string) => void;
  template?: WorkflowTemplate | null;
  mode: 'view' | 'add' | 'edit';
  selectedCountry: string;
  countryLabel: string;
  onError?: (error: any) => void;
}> = ({ isOpen, onClose, onSave, template, mode, selectedCountry, countryLabel, onError }) => {
  const [, setFieldErrors] = useState<Record<string, string>>({});
  const [, setIsSaving] = useState(false);

  const [formData, setFormData] = useState<WorkflowTemplateForm>({
    name: '',
    description: '',
    country: selectedCountry,
    type: 'OTHER',
    priority: 'medium',
    estimatedHours: 0,
    billing: {
      feeType: 'HOURLY',
      fixedAmount: 0,
      billableRate: 0,
    },
    checklist: [] as ChecklistItem[],
    tags: [] as string[],
    recurrence: {
      isRecurring: false,
      frequency: 'MONTHLY',
      interval: 1,
      dayOfWeek: undefined as number | undefined,
      dayOfMonth: undefined as number | undefined,
      monthOfYear: undefined as number | undefined,
      endDate: undefined as string | undefined,
      maxOccurrences: undefined as number | undefined,
    },
    isActive: true,
  });
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newTag, setNewTag] = useState('');
  const [modalError, setModalError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      if ((mode === 'edit' || mode === 'view') && template) {
        setFormData({
          name: template.name || '',
          description: template.description || '',
          country: template.country || selectedCountry,
          type: template.type || 'OTHER',
          priority: template.priority || 'medium',
          estimatedHours: template.estimatedHours || 0,
          billing: {
            feeType: template.billing?.feeType || 'HOURLY',
            fixedAmount: template.billing?.fixedAmount || 0,
            billableRate: template.billing?.billableRate || 0,
          },
          checklist: Array.isArray(template.checklist) ? template.checklist : [],
          tags: Array.isArray(template.tags) ? template.tags : [],
          recurrence: {
            isRecurring: template.recurrence?.isRecurring || false,
            frequency: template.recurrence?.frequency || 'MONTHLY',
            interval: template.recurrence?.interval || 1,
            dayOfWeek: template.recurrence?.dayOfWeek,
            dayOfMonth: template.recurrence?.dayOfMonth,
            monthOfYear: template.recurrence?.monthOfYear,
            endDate: template.recurrence?.endDate,
            maxOccurrences: template.recurrence?.maxOccurrences,
          },
          isActive: template.isActive !== undefined ? template.isActive : true,
        });
      } else {
        // Reset form for add mode
        setFormData({
          name: '',
          description: '',
          country: selectedCountry,
          type: 'OTHER',
          priority: 'medium',
          estimatedHours: 0,
          billing: {
            feeType: 'HOURLY',
            fixedAmount: 0,
            billableRate: 0,
          },
          checklist: [],
          tags: [],
          recurrence: {
            isRecurring: false,
            frequency: 'MONTHLY',
            interval: 1,
            dayOfWeek: undefined,
            dayOfMonth: undefined,
            monthOfYear: undefined,
            endDate: undefined,
            maxOccurrences: undefined,
          },
          isActive: true,
        });
      }
    }
  }, [isOpen, mode, template, selectedCountry]);

  const handleError = (error: any) => {
    const errorDetails = error?.response?.data?.error || error?.response?.data;
    if (errorDetails?.details && Array.isArray(errorDetails.details)) {
      // Parse field-specific validation errors
      const errors: Record<string, string> = {};
      errorDetails.details.forEach((err: any) => {
        const field = err.field || err.path || 'unknown';
        errors[field] = err.message;
      });
      setFieldErrors(errors);
      setModalError('Please fix the validation errors below');
    } else {
      setModalError(errorDetails?.message || error?.message || 'Failed to save template');
    }
  };

  const handleAddChecklistItem = () => {
    if (newChecklistItem.trim()) {
      const newItem: ChecklistItem = {
        label: newChecklistItem.trim(),
        description: '',
        required: false,
        order: formData.checklist.length
      };
      setFormData(prev => ({
        ...prev,
        checklist: [...prev.checklist, newItem]
      }));
      setNewChecklistItem('');
    }
  };

  const handleRemoveChecklistItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('=== handleSubmit called ===');
    e.preventDefault();
    console.log('Form prevented default, mode:', mode, 'template._id:', template?._id);
    
    if (!formData.name.trim()) {
      setModalError('Please enter a workflow name');
      return;
    }

    setModalError('');
    setFieldErrors({});

    // Map frontend data structure to backend structure
    const workflowData: any = {
      country: formData.country,
      name: formData.name.trim(),
      description: formData.description || '',
      type: formData.type || 'OTHER',
      priority: formData.priority || 'medium', // Must be lowercase
      estimatedHours: formData.estimatedHours || 0,
      billing: {
        feeType: formData.billing.feeType,
        ...(formData.billing.feeType === 'FIXED' ? { fixedAmount: formData.billing.fixedAmount } : {}),
        ...(formData.billing.feeType === 'HOURLY' ? { billableRate: formData.billing.billableRate } : {}),
      },
      checklist: formData.checklist || [],
      tags: formData.tags || [],
      recurrence: formData.recurrence && formData.recurrence.isRecurring ? {
        isRecurring: true,
        frequency: formData.recurrence.frequency,
        interval: formData.recurrence.interval,
        dayOfWeek: formData.recurrence.dayOfWeek,
        dayOfMonth: formData.recurrence.dayOfMonth,
        monthOfYear: formData.recurrence.monthOfYear,
        endDate: formData.recurrence.endDate,
        maxOccurrences: formData.recurrence.maxOccurrences,
      } : {
        isRecurring: false,
        frequency: null
      },
      isActive: formData.isActive !== undefined ? formData.isActive : true,
    };

    // Store id separately for edit mode URL, DON'T include in request body
    const templateIdForUrl = template?.id || template?._id;
    console.log('Template ID for URL:', templateIdForUrl);

    console.log('Built workflow data:', workflowData);
    setIsSaving(true);
    try {
      console.log('Calling onSave with data:', workflowData, 'ID:', templateIdForUrl);
      await onSave(workflowData, templateIdForUrl);
      console.log('onSave completed successfully');
      setIsSaving(false);
    } catch (err: any) {
      console.error('Error caught in handleSubmit:', err);
      setIsSaving(false);
      handleError(err);
    }
  };

  if (!isOpen) return null;

  const isReadOnly = mode === 'view';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {mode === 'view' ? 'View Workflow Template' : mode === 'add' ? 'Add Workflow Template' : 'Edit Workflow Template'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Messages */}
          {modalError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-red-900">Error</h3>
                <p className="text-sm text-red-700 mt-1">{modalError}</p>
              </div>
            </div>
          )}

          {/* Country Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <div className="text-base font-semibold text-gray-900">{countryLabel}</div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Workflow Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Canada - Client Onboarding & KYC"
              required
              disabled={isReadOnly}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder="Brief description of this workflow"
              disabled={isReadOnly}
            />
          </div>

          {/* Type, Priority, Estimated Hours */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="TAX_RETURN">Tax Return</option>
                <option value="BOOKKEEPING">Bookkeeping</option>
                <option value="PAYROLL">Payroll</option>
                <option value="AUDIT">Audit</option>
                <option value="CONSULTATION">Consultation</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Estimated Hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Hours
              </label>
              <input
                type="number"
                value={formData.estimatedHours}
                onChange={(e) => setFormData(prev => ({ ...prev, estimatedHours: Number(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0"
                min="0"
                disabled={isReadOnly}
              />
            </div>
          </div>

          {/* Billing Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-4">Billing Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Fee Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fee Type
                </label>
                <select
                  value={formData.billing.feeType}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billing: { ...prev.billing, feeType: e.target.value }
                  }))}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="HOURLY">Hourly</option>
                  <option value="FIXED">Fixed</option>
                  <option value="NO_CHARGE">No Charge</option>
                </select>
              </div>

              {/* Billable Amount/Rate */}
              {formData.billing.feeType === 'FIXED' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fixed Amount ($)
                  </label>
                  <input
                    type="number"
                    value={formData.billing.fixedAmount}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      billing: { ...prev.billing, fixedAmount: Number(e.target.value) }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0"
                    min="0"
                    disabled={isReadOnly}
                  />
                </div>
              ) : formData.billing.feeType === 'HOURLY' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billable Rate ($/hr)
                  </label>
                  <input
                    type="number"
                    value={formData.billing.billableRate}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      billing: { ...prev.billing, billableRate: Number(e.target.value) }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0"
                    min="0"
                    disabled={isReadOnly}
                  />
                </div>
              ) : (
                <div className="pt-6 text-sm text-gray-600">No charge</div>
              )}
            </div>
          </div>

          {/* Recurrence Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-4">Recurrence Information</h4>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={formData.recurrence.isRecurring}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    recurrence: { ...prev.recurrence, isRecurring: e.target.checked }
                  }))}
                  disabled={isReadOnly}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="isRecurring" className="ml-2 block text-sm text-gray-900">
                  This workflow is recurring
                </label>
              </div>

              {formData.recurrence.isRecurring && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Frequency
                    </label>
                    <select
                      value={formData.recurrence.frequency}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurrence: { ...prev.recurrence, frequency: e.target.value }
                      }))}
                      disabled={isReadOnly}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="BI_WEEKLY">Bi-Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="ANNUALLY">Annually</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interval
                    </label>
                    <input
                      type="number"
                      value={formData.recurrence.interval}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurrence: { ...prev.recurrence, interval: Number(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="1"
                      min="1"
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Checklist */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Checklist Items
            </label>
            <div className="space-y-2">
              {formData.checklist.map((item, index) => (
                <div key={`checklist-${index}-${item.label}`} className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="flex-1 text-sm text-gray-900">{item.label}</span>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => handleRemoveChecklistItem(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {!isReadOnly && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddChecklistItem())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Add checklist item..."
                  />
                  <button
                    type="button"
                    onClick={handleAddChecklistItem}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, tagIndex) => (
                  <span key={`tag-${tagIndex}-${tag}`} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                    {String(tag)}
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-gray-500 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {!isReadOnly && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Add tag..."
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Active Status */}
          {!isReadOnly && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active workflow template
              </label>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                console.log('=== Close/Cancel button clicked ===');
                onClose();
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </button>
            {!isReadOnly && (
              <button
                type="submit"
                onClick={() => console.log('=== Submit button clicked (before form submission) ===')}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {mode === 'add' ? 'Create Workflow' : 'Save Changes'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export const CountryWorkflowManagement: React.FC = () => {
  const [templates, setTemplates] = useState<CountryTemplates>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string>('canada');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'add' | 'edit'>('view');
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);

  const countries = ['canada', 'usa', 'india', 'uae', 'germany', 'france', 'australia'];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/workflows/country-templates');
      
      // Group templates by country
      const grouped: CountryTemplates = {};
      countries.forEach(country => {
        grouped[country] = [];
      });
      
      if (response.data?.data) {
        (response.data.data as WorkflowTemplate[]).forEach(template => {
          if (!grouped[template.country]) {
            grouped[template.country] = [];
          }
          grouped[template.country].push(template);
        });
      }
      
      setTemplates(grouped);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Failed to load workflow templates';
      setError(errorMessage);
      console.error('Error loading workflow templates:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.url
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      setSaving(true);
      setError('');
      
      await api.post('/workflows/seed-defaults');
      
      setSuccessMessage('Default templates seeded successfully');
      await loadTemplates();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to seed default templates');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId?: string) => {
    if (!templateId) {
      setError('Missing template id');
      return;
    }
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      await api.delete(`/workflows/country-templates/${templateId}`);
      
      setSuccessMessage('Template deleted successfully');
      await loadTemplates();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to delete template');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleViewTemplate = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setModalMode('view');
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleAddTemplate = () => {
    setSelectedTemplate(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleSaveTemplate = async (templateData: Partial<WorkflowTemplate>, templateId?: string) => {
    console.log('=== handleSaveTemplate called ===');
    console.log('Mode:', modalMode);
    console.log('Template data received:', templateData);
    console.log('Template ID:', templateId);
    try {
      setSaving(true);
      setError('');

      if (modalMode === 'add') {
        console.log('POST request to /workflows/country-templates');
        await api.post('/workflows/country-templates', templateData);
        setSuccessMessage('Template created successfully');
      } else if (modalMode === 'edit' && templateId) {
        console.log('PUT request to /workflows/country-templates/' + templateId);
        await api.put(`/workflows/country-templates/${templateId}`, templateData);
        setSuccessMessage('Template updated successfully');
      } else {
        console.log('No action taken - mode is', modalMode, 'and templateId is', templateId);
      }

      console.log('API call completed, loading templates...');
      await loadTemplates();
      console.log('Templates loaded, closing modal');
      setIsModalOpen(false);
      setSelectedTemplate(null);
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      // Re-throw error so modal can handle it
      console.error('Error saving template:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const countryLabels: Record<string, string> = {
    canada: '🇨🇦 Canada',
    usa: '🇺🇸 United States',
    india: '🇮🇳 India',
    uae: '🇦🇪 United Arab Emirates',
    germany: '🇩🇪 Germany',
    france: '🇫🇷 France',
    australia: '🇦🇺 Australia'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading country workflow templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-red-900">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-green-900">Success</h3>
            <p className="text-sm text-green-700 mt-1">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Country Tabs */}
      <div className="border-b border-gray-200 flex items-center justify-between">
        <div className="flex gap-1 overflow-x-auto">
          {countries.map(country => (
            <button
              key={country}
              onClick={() => setSelectedCountry(country)}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                selectedCountry === country
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {countryLabels[country]}
              {templates[country]?.length > 0 && (
                <span className="ml-2 bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                  {templates[country].length}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={handleSeedDefaults}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors ml-4 flex-shrink-0"
        >
          <RefreshCw className="w-4 h-4" />
          Seed Defaults
        </button>
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button
            onClick={handleAddTemplate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors ml-auto"
          >
            <Plus className="w-4 h-4" />
            Add Template
          </button>
        </div>

        {templates[selectedCountry]?.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-4">No templates found for {countryLabels[selectedCountry]}</p>
            <button
              onClick={handleAddTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create First Template
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates[selectedCountry]?.map(template => (
              <div key={template.id || template._id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 
                        onClick={() => handleViewTemplate(template)}
                        className="font-semibold text-gray-900 cursor-pointer hover:text-primary-600 transition-colors"
                      >
                        {template.name}
                      </h3>
                      {template.isActive ? (
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                      ) : (
                        <span className="inline-block w-2 h-2 bg-gray-400 rounded-full"></span>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                      {template.billingPeriod && (
                        <span>📅 {template.billingPeriod}</span>
                      )}
                      {template.recurrencePattern && (
                        <span>🔄 {template.recurrencePattern}</span>
                      )}
                      {template.checklist && Array.isArray(template.checklist) && (
                        <span>✓ {template.checklist.length} items</span>
                      )}
                      <span>v{template.version}</span>
                    </div>
                    {template.tags && Array.isArray(template.tags) && template.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {template.tags.map((tag, tagIndex) => (
                          <span key={`template-tag-${template.id || template._id}-${tagIndex}`} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {String(tag)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleViewTemplate(template)}
                      className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      title="View template"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Edit template"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id || template._id)}
                      disabled={saving}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                      title="Delete template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">How It Works</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Master templates are managed here and apply to all new firms in that country</li>
          <li>• When creating a new firm, templates are automatically copied to the firm</li>
          <li>• Firms can customize their copied templates independently</li>
          <li>• Use "Seed Defaults" to initialize default workflows for all countries</li>
        </ul>
      </div>

      {/* Workflow Modal */}
      <WorkflowModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTemplate}
        template={selectedTemplate}
        mode={modalMode}
        selectedCountry={selectedCountry}
        countryLabel={countryLabels[selectedCountry]}
      />
    </div>
  );
};
